import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Image,
  LoadingOverlay,
  Menu,
  Pagination,
  ScrollArea,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import { IconSearch } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { getDataStockPhoto } from "../../../../api/ApiProduct";
import cx from "clsx";
import classes from "../../../../Styles/TableScrollArea.module.css";
import { TblItemImageCommand } from "../../../../model/ProductList";

const initValueImage: TblItemImageCommand = {
  id: 0,
  imageCode: "", // Mã hình ảnh
  imageName: "", // Tên hình ảnh
  description: "", // Mô tả hình ảnh
  orderNumber: 0, // Số thứ tự của hình ảnh
  orgId: 0, // ID của tổ chức liên quan đến hình ảnh
  invItemId: 0, // ID của sản phẩm liên kết với hình ảnh
  image: "", // Dữ liệu hình ảnh (URL hoặc file)
  imagePath: "", // Đường dẫn của hình ảnh
  primaryImg: "N", // Xác định hình ảnh chính ("Y" cho ảnh chính, "N" cho ảnh phụ)
  creationDate: "", // Ngày tạo hình ảnh
  createdBy: "", // Người tạo hình ảnh
  lastUpdateDate: "", // Ngày cập nhật cuối cùng của hình ảnh
  lastUpdatedBy: "", // Người cập nhật cuối cùng của hình ảnh
  lastUpdateLogin: "", // Thông tin đăng nhập của lần cập nhật cuối cùng
  imageWidth: 0, // Chiều rộng của hình ảnh (pixel)
  imageHeight: 0, // Chiều cao của hình ảnh (pixel)
};

const StockPhoto = ({
  dataImage,
  addImage,
  isDescriptionItem,
}: {
  dataImage: any[];
  addImage: any;
  isDescriptionItem?: boolean;
}) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [activePage, setPage] = useState(1);
  const [visible, { toggle, open, close }] = useDisclosure(false);
  const [scrolled, setScrolled] = useState(false);

  const [search, setSearch] = useState({});
  const [keySearch, setKeySearch] = useDebouncedState<string>("", 300);
  const [itemCode, setItemCode] = useDebouncedState<string>("", 300);
  const [itemName, setItemName] = useDebouncedState<string>("", 300);
  const [dataImageProps, setDataImageProps] = useState<any[]>(dataImage);

  const fetchData = async () => {
    open();
    try {
      let url = `?Skip=${(activePage - 1) * 20}&Take=${20}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      if (itemCode.length > 0) {
        url += `&ItemCode=${itemCode?.trim()?.toUpperCase()}`;
      }

      if (itemName.length > 0) {
        url += `&ItemName=${itemName?.trim()}`;
      }

      url += `&${params.toString()}`;

      const res = await getDataStockPhoto(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      console.error(error);
      return;
    } finally {
      close();
    }
  };

  useEffect(() => {
    fetchData();
  }, [activePage]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (210 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <Box w={"82.5vw"} maw={1550}>
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 5 }}
      />
      <ScrollArea
        h={height}
        onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
        offsetScrollbars
      >
        <Grid
          mt={10}
          w={"100%"}
          style={{ zIndex: 1000 }}
          className={cx(classes.header, {
            [classes.scrolled]: scrolled,
          })}
        >
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập mã sản phẩm"
              defaultValue={itemCode}
              onChange={(e) => setItemCode(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập tên sản phẩm"
              defaultValue={itemName}
              onChange={(e) => setItemName(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
        <Grid mt={10}>
          {data &&
            data.length > 0 &&
            data.map((item) => (
              <Grid.Col span={{ base: 12, md: 6, lg: 2.4 }} key={item.id}>
                <Card
                  shadow="sm"
                  padding="lg"
                  radius="md"
                  withBorder
                  style={{
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  <Card.Section>
                    <Box
                      style={{
                        width: "100%",
                        height: "160px",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "5px",
                      }}
                    >
                      <Image
                        // src={item.imagePath + item.image}
                        src={`${item.imagePath ? item.imagePath : ""}${
                          item.image
                        }`}
                        height={160}
                        alt=""
                      />
                    </Box>
                  </Card.Section>

                  <Flex direction={"column"} justify="space-between" h={300}>
                    <Flex direction={"column"} gap={"xs"} align={"center"}>
                      <Box ta={"center"}>
                        <Tooltip label={item.itemName}>
                          <Text fw={500} lineClamp={2}>
                            {item.itemName}
                          </Text>
                        </Tooltip>
                      </Box>
                      <Badge color="blue">{item.itemCode}</Badge>
                    </Flex>
                    {dataImageProps.filter(
                      (i: any) =>
                        i.image ===
                        `${item.imagePath ? item.imagePath : ""}${item.image}`
                    ).length > 0 ? (
                      <Button
                        color="red"
                        radius="md"
                        onClick={() => {
                          const dataDelete = dataImageProps.filter(
                            (i) =>
                              i.image !==
                              `${item.imagePath ? item.imagePath : ""}${
                                item.image
                              }`
                          );

                          setDataImageProps(dataDelete);
                          addImage(dataDelete);
                        }}
                      >
                        Bỏ chọn
                      </Button>
                    ) : !isDescriptionItem ? (
                      <Menu width={"target"}>
                        <Menu.Target>
                          <Button color="blue" radius="md">
                            Dùng ảnh này
                          </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Label>Chọn loại cho ảnh</Menu.Label>
                          <Menu.Item
                            onClick={() => {
                              addImage([
                                ...dataImageProps,
                                {
                                  ...initValueImage,
                                  id: item.id,
                                  image: `${
                                    item.imagePath ? item.imagePath : ""
                                  }${item.image}`,
                                  imageName: item.imageName,
                                  orgId: 1,
                                },
                              ]);
                              setDataImageProps((prev) => [
                                ...prev,
                                {
                                  ...initValueImage,
                                  id: item.id,
                                  image: `${
                                    item.imagePath ? item.imagePath : ""
                                  }${item.image}`,
                                  imageName: item.imageName,
                                  orgId: 1,
                                },
                              ]);
                            }}
                          >
                            Hình sản phẩm
                          </Menu.Item>
                          <Menu.Item
                            onClick={() => {
                              addImage([
                                ...dataImageProps,
                                {
                                  ...initValueImage,
                                  id: item.id,
                                  image: `${
                                    item.imagePath ? item.imagePath : ""
                                  }${item.image}`,
                                  imageName: item.imageName,
                                  orgId: 2,
                                },
                              ]);
                              setDataImageProps((prev) => [
                                ...prev,
                                {
                                  ...initValueImage,
                                  id: item.id,
                                  image: `${
                                    item.imagePath ? item.imagePath : ""
                                  }${item.image}`,
                                  imageName: item.imageName,
                                  orgId: 2,
                                },
                              ]);
                            }}
                          >
                            Khách hàng chụp
                          </Menu.Item>
                          <Menu.Item
                            onClick={() => {
                              addImage([
                                ...dataImageProps,
                                {
                                  ...initValueImage,
                                  id: item.id,
                                  image: `${
                                    item.imagePath ? item.imagePath : ""
                                  }${item.image}`,
                                  imageName: item.imageName,
                                  orgId: 3,
                                },
                              ]);
                              setDataImageProps((prev) => [
                                ...prev,
                                {
                                  ...initValueImage,
                                  id: item.id,
                                  image: `${
                                    item.imagePath ? item.imagePath : ""
                                  }${item.image}`,
                                  imageName: item.imageName,
                                  orgId: 3,
                                },
                              ]);
                            }}
                          >
                            HACOM tự chụp
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    ) : (
                      <Button
                        color="blue"
                        radius="md"
                        onClick={() => {
                          addImage([
                            ...dataImageProps,
                            {
                              image: `${item.imagePath ? item.imagePath : ""}${
                                item.image
                              }`,
                              imageName: item.imageName,
                            },
                          ]);
                          setDataImageProps((prev) => [
                            ...prev,
                            {
                              image: `${item.imagePath ? item.imagePath : ""}${
                                item.image
                              }`,
                              imageName: item.imageName,
                            },
                          ]);
                        }}
                      >
                        Dùng ảnh này
                      </Button>
                    )}
                  </Flex>
                </Card>
              </Grid.Col>
            ))}
        </Grid>
      </ScrollArea>
      <Flex justify={"end"}>
        <Pagination
          mt={10}
          mr={10}
          value={activePage}
          onChange={setPage}
          total={
            rowCount % 20 === 0 ? rowCount / 20 : Math.floor(rowCount / 20) + 1
          }
        />
      </Flex>
    </Box>
  );
};

export default StockPhoto;
